import React, { useEffect, useState } from "react";
import { ExternalLink, KeyRound, RefreshCw, Save } from "lucide-react";
import { toast } from "react-hot-toast";
import api, {
  getPlatformIntegrations,
  updatePlatformIntegration,
  getLocationIntegrations,
  updateLocationIntegration,
} from "../../services/api";
import { getTokenInfo } from "../../utils/auth";

// Location admins manage their own restaurant's keys — only those are used.
// The server returns just the services that work in the platform country.
const LOCATION_PAGE_KEYS: ProviderKey[] = ["mail", "square", "stripe", "razorpay", "qrCode"];

// Public, non-secret values (shown to customers), so plain text inputs.
const PLAIN_TEXT_FIELDS = new Set(["QR_UPI_ID", "QR_PAYEE_NAME"]);

type ProviderKey =
  | "baseEnv"
  | "mail"
  | "applePasskit"
  | "digitalOceanSpaces"
  | "square"
  | "stripe"
  | "razorpay"
  | "qrCode";

type ProviderStatus = {
  provider: ProviderKey;
  label: string;
  configured: boolean;
  accountEmail: string;
  updatedAt: string | null;
  env: Record<string, string>;
};

const PROVIDERS: Array<{
  key: ProviderKey;
  title: string;
  description: string;
  docsUrl: string;
  signupUrl: string;
  accountHelp: string;
  fields: Array<{ key: string; label: string; required: boolean }>;
}> = [
  {
    key: "mail",
    title: "Mail",
    description: `Configure mail provider key (Resend) and sender settings used for OTP and promotions.Add ${process.env.REACT_APP_API_URL} as BASE_URL`,
    docsUrl: "https://resend.com/docs",
    signupUrl: "https://resend.com/",
    accountHelp: "Use the email for your mail service account.",
    fields: [
      { key: "MAIL_KEY", label: "MAIL_KEY", required: true },
      { key: "MAIL_FROM", label: "MAIL_FROM", required: true },
      { key: "BASE_URL", label: "BASE_URL", required: false },
    ],
  },
  {
    key: "applePasskit",
    title: "Apple PassKit",
    description: `Configure Apple PassKit certificate password and base URL. Add ${process.env.REACT_APP_API_URL} as BASE_URL.`,
    docsUrl: "https://developer.apple.com/wallet/",
    signupUrl: "https://developer.apple.com/account/",
    accountHelp: "Use the Apple developer account email.",
    fields: [
      {
        key: "PASS_CERTIFICATE_PASSWORD",
        label: "PASS_CERTIFICATE_PASSWORD",
        required: true,
      },
      { key: "BASE_URL", label: "BASE_URL", required: false },
    ],
  },
  // {
  //   key: "digitalOceanSpaces",
  //   title: "DigitalOcean Spaces",
  //   description:
  //     "Configure object storage credentials and bucket details for uploads.",
  //   docsUrl: "https://docs.digitalocean.com/products/spaces/",
  //   signupUrl: "https://cloud.digitalocean.com/",
  //   accountHelp: "Use your DigitalOcean account email.",
  //   fields: [
  //     { key: "DO_SPACES_KEY", label: "DO_SPACES_KEY", required: true },
  //     { key: "DO_SPACES_SECRET", label: "DO_SPACES_SECRET", required: true },
  //     {
  //       key: "DO_SPACES_ENDPOINT",
  //       label: "DO_SPACES_ENDPOINT",
  //       required: true,
  //     },
  //     { key: "DO_SPACES_BUCKET", label: "DO_SPACES_BUCKET", required: true },
  //     { key: "DO_SPACES_REGION", label: "DO_SPACES_REGION", required: true },
  //   ],
  // },
  {
    key: "square",
    title: "Square (Platform)",
    description:
      "Platform-level Square credentials used as a fallback when a location has no Square keys configured. Per-location keys always take priority.",
    docsUrl: "https://developer.squareup.com/docs",
    signupUrl: "https://squareup.com/us/en/developers",
    accountHelp: "Use your Square Developer account email.",
    fields: [
      {
        key: "SQUARE_ACCESS_TOKEN",
        label: "SQUARE_ACCESS_TOKEN",
        required: true,
      },
      {
        key: "SQUARE_LOCATION_ID",
        label: "SQUARE_LOCATION_ID",
        required: true,
      },
      {
        key: "SQUARE_APPLICATION_ID",
        label: "SQUARE_APPLICATION_ID",
        required: true,
      },
      {
        key: "SQUARE_ENVIRONMENT",
        label: "SQUARE_ENVIRONMENT",
        required: true,
      },
      {
        key: "SQUARE_WEBHOOK_SIGNATURE_KEY",
        label: "SQUARE_WEBHOOK_SIGNATURE_KEY",
        required: false,
      },
      {
        key: "SQUARE_BASE_URL",
        label: "SQUARE_BASE_URL (Webhook URL)",
        required: true,
      },
    ],
  },
  {
    key: "stripe",
    title: "Stripe",
    description:
      "Card payments on the POS through your Stripe account. Both keys are needed: the secret key for the server and the publishable key for the payment form.",
    docsUrl: "https://docs.stripe.com/keys",
    signupUrl: "https://dashboard.stripe.com/register",
    accountHelp: "Use your Stripe account email.",
    fields: [
      { key: "STRIPE_SECRET_KEY", label: "STRIPE_SECRET_KEY (sk_...)", required: true },
      { key: "STRIPE_PUBLISHABLE_KEY", label: "STRIPE_PUBLISHABLE_KEY (pk_...)", required: true },
      { key: "WEBHOOK_ENDPOINT_SECRET", label: "WEBHOOK_ENDPOINT_SECRET", required: false },
    ],
  },
  {
    key: "razorpay",
    title: "Razorpay",
    description:
      "UPI, card, netbanking and wallet payments on the POS for restaurants in India, through your own Razorpay account.",
    docsUrl: "https://razorpay.com/docs/payments/dashboard/account-settings/api-keys/",
    signupUrl: "https://dashboard.razorpay.com/signup",
    accountHelp: "Use your Razorpay account email.",
    fields: [
      { key: "RAZORPAY_KEY_ID", label: "RAZORPAY_KEY_ID (rzp_...)", required: true },
      { key: "RAZORPAY_KEY_SECRET", label: "RAZORPAY_KEY_SECRET", required: true },
    ],
  },
  {
    key: "qrCode",
    title: "QR Code Payment",
    description:
      "Enter your UPI ID and the POS creates a QR for every order with the amount already filled in — no image needed. Customers scan and pay, and staff confirm the payment in Approve Orders before the order goes through.",
    docsUrl: "https://www.npci.org.in/what-we-do/upi/product-overview",
    signupUrl: "https://www.npci.org.in/what-we-do/upi/live-members",
    accountHelp: "Optional: email of the account the QR pays into.",
    // QR_IMAGE_URL (optional) is set by the image upload below the fields.
    fields: [
      { key: "QR_UPI_ID", label: "UPI ID (e.g. shop@okhdfc)", required: true },
      { key: "QR_PAYEE_NAME", label: "Payee name shown to customers", required: false },
    ],
  },
];

const createDefaultForms = () =>
  PROVIDERS.reduce(
    (acc, provider) => {
      acc[provider.key] = { accountEmail: "", credentials: {} };
      return acc;
    },
    {} as Record<
      ProviderKey,
      { accountEmail: string; credentials: Record<string, string> }
    >,
  );

/** Upload/preview of the restaurant's payment QR image. */
const QrImageField: React.FC<{ currentUrl: string; onUploaded: (url: string) => void }> = ({
  currentUrl,
  onUploaded,
}) => {
  const [uploading, setUploading] = useState(false);

  const onFile = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }
    const body = new FormData();
    body.append("image", file);
    setUploading(true);
    try {
      const res = await api.post<{ url: string }>("/upload-image", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(res.data.url);
      toast.success("QR uploaded — click Save Credentials to apply it");
    } catch {
      toast.error("QR upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Or upload your own QR image (optional)
      </label>
      <p className="mb-2 text-xs text-gray-500">
        Only needed if you don't use a UPI ID — an uploaded QR can't include the order amount.
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-40 w-40 items-center justify-center overflow-hidden rounded-md border border-dashed border-gray-300 bg-gray-50">
          {currentUrl ? (
            <img src={currentUrl} alt="Payment QR" className="h-full w-full object-contain" />
          ) : (
            <span className="px-2 text-center text-xs text-gray-400">No QR uploaded</span>
          )}
        </div>
        <label className="cursor-pointer rounded-md border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50">
          {uploading ? "Uploading..." : currentUrl ? "Replace QR" : "Upload QR"}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={uploading}
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </label>
      </div>
    </div>
  );
};

const DeveloperIntegrations: React.FC = () => {
  const tokenInfo = getTokenInfo();
  const restaurantId: string | undefined =
    tokenInfo?.role !== "master_admin" ? tokenInfo?.restaurantId : undefined;
  const [statusMap, setStatusMap] = useState<
    Record<ProviderKey, ProviderStatus>
  >({} as Record<ProviderKey, ProviderStatus>);
  const visibleProviders = restaurantId
    ? PROVIDERS.filter((p) => LOCATION_PAGE_KEYS.includes(p.key) && statusMap[p.key]).map((p) => ({
        ...p,
        title: p.title.replace(" (Platform)", ""),
      }))
    : PROVIDERS;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<ProviderKey | null>(null);
  const [forms, setForms] =
    useState<
      Record<
        ProviderKey,
        { accountEmail: string; credentials: Record<string, string> }
      >
    >(createDefaultForms());

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const res = restaurantId
        ? await getLocationIntegrations(restaurantId)
        : await getPlatformIntegrations();
      const list = (res.data?.data || []) as ProviderStatus[];
      const nextStatusMap = {} as Record<ProviderKey, ProviderStatus>;
      const nextForms = createDefaultForms();

      list.forEach((item) => {
        nextStatusMap[item.provider] = item;
        if (nextForms[item.provider]) {
          nextForms[item.provider].accountEmail = item.accountEmail || "";
        }
      });

      setStatusMap(nextStatusMap);
      setForms(nextForms);
    } catch (error) {
      toast.error("Failed to load integration settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const onFieldChange = (provider: ProviderKey, key: string, value: string) => {
    setForms((prev) => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        credentials: {
          ...prev[provider].credentials,
          [key]: value,
        },
      },
    }));
  };

  const onEmailChange = (provider: ProviderKey, value: string) => {
    setForms((prev) => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        accountEmail: value,
      },
    }));
  };

  const onSave = async (provider: ProviderKey) => {
    setSaving(provider);
    try {
      if (restaurantId) {
        await updateLocationIntegration(restaurantId, provider as any, forms[provider]);
      } else {
        await updatePlatformIntegration(provider as any, forms[provider]);
      }
      toast.success("Credentials saved securely");
      setForms((prev) => ({
        ...prev,
        [provider]: {
          ...prev[provider],
          credentials: {},
        },
      }));
      await fetchIntegrations();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to save credentials");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <KeyRound size={28} className="text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-800 m-0">
            Integration & Environment Keys
          </h1>
        </div>
        <button
          onClick={fetchIntegrations}
          className="h-10 w-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
          title="Refresh"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      <p className="text-gray-600 mb-6">
        Save credentials dynamically from Admin. Values are encrypted in the
        backend database and used as runtime fallbacks when available.
      </p>

      <div className="space-y-6">
        {visibleProviders.map((provider) => {
          const status = statusMap[provider.key];
          const form = forms[provider.key];

          return (
            <div
              key={provider.key}
              className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            >
              <div className="flex flex-col items-start md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {provider.title}
                  </h2>
                  <p className="text-gray-600">{provider.description}</p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    status?.configured
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {status?.configured ? "Configured" : "Not Configured"}
                </span>
              </div>

              <div className="flex flex-wrap gap-3 mb-5">
                <a
                  href={provider.signupUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  Open Provider <ExternalLink size={16} />
                </a>
                <a
                  href={provider.docsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Docs <ExternalLink size={16} />
                </a>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Email
                </label>
                <input
                  type="email"
                  value={form.accountEmail}
                  onChange={(e) => onEmailChange(provider.key, e.target.value)}
                  placeholder={provider.accountHelp}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {provider.fields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                      {field.required ? " *" : ""}
                    </label>
                    <input
                      type={PLAIN_TEXT_FIELDS.has(field.key) ? "text" : "password"}
                      value={form.credentials[field.key] || ""}
                      onChange={(e) =>
                        onFieldChange(provider.key, field.key, e.target.value)
                      }
                      placeholder={
                        status?.env?.[field.key]
                          ? `Existing: ${status.env[field.key]}`
                          : "Paste value"
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>

              {provider.key === "qrCode" && (
                <QrImageField
                  currentUrl={form.credentials.QR_IMAGE_URL || status?.env?.QR_IMAGE_URL || ""}
                  onUploaded={(url) => onFieldChange("qrCode", "QR_IMAGE_URL", url)}
                />
              )}

              {status?.updatedAt && (
                <p className="text-xs text-gray-500 mb-4">
                  Last updated: {new Date(status.updatedAt).toLocaleString()}
                </p>
              )}

              <button
                onClick={() => onSave(provider.key)}
                disabled={saving === provider.key}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
              >
                <Save size={16} />
                {saving === provider.key ? "Saving..." : "Save Credentials"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeveloperIntegrations;
